/**
 * ThemeMapView — la Mappa dei temi in DUE modalità complementari.
 *
 *   ★ "Nel tempo"  (default) — il tema scelto è un FILO che attraversa i secoli:
 *      i suoi autori-sfera sono disposti su UNA riga in ordine cronologico,
 *      con spaziatura generosa (mai impilati) e scorrimento orizzontale se sono
 *      molti. Fasce di secolo sullo sfondo. Accessori dedicati:
 *        · "Racconta"  → passo-passo che accende gli autori da sx a dx col blurb;
 *        · frecce di trasmissione (relations.influenced) → chi ha influenzato chi.
 *
 *   ● "Confronto" — il tema al centro, gli autori in cerchio sullo stesso piano.
 *      Accessorio dedicato: selezioni 2-3 autori e a lato si affiancano i loro
 *      trattamenti (compare & contrast).
 *
 * Una barra di temi (rail) fa da indice/selettore. Cliccare un autore apre il
 * suo trattamento di quel tema (navigate:author { id, themeId }) — deep-link
 * già consumato da AuthorDetailView.
 *
 * Tutto front-end: legge i dati statici via getDataService(); nessun backend.
 */

import { BaseComponent } from '@/core/BaseComponent';
import { getDataService } from '@/services/data-registry';
import type { AuthorCatalog, ThemeTreatmentBlurb, ThemeId } from '@/data/schema';
import { themeLabel, themeKind, nodeKindLabel } from '@/data/vocab';
import type { UiLocale } from '@/data/vocab';
import { CATEGORY_COLORS } from '@/data/category-colors';
import { computeInitials } from '@/data/initials';
import { chronoYear } from '@/data/chronology';
import { authorSphereColor, authorSphereDefs, authorSphere } from './author-sphere';
import { escapeXml } from '@/core/escape';

type Mode = 'time' | 'compare';

/** Stato serializzabile della vista → il router lo cattura prima di aprire un
 *  autore e lo ripristina al "← Indietro", così non si perde il filo. */
export interface ThemeMapState {
  mode: Mode;
  selectedTheme: string | null;
  compare: string[];
  narrOn: boolean;
  narrI: number;
}

interface Placed { author: AuthorCatalog; year: number; dated: boolean; x: number; }

const ROMAN: Record<number, string> = { 14: 'XIV', 15: 'XV', 16: 'XVI', 17: 'XVII', 18: 'XVIII', 19: 'XIX', 20: 'XX', 21: 'XXI' };

const MIN_GAP = 150;   // distanza minima tra autori (oltre → scroll, mai appiccicati)
const NODE_R = 23;

export class ThemeMapView extends BaseComponent {
  private mode: Mode = 'time';
  private selectedTheme: string | null = null;
  private narr = { on: false, i: 0 };
  private compare = new Set<string>();
  private resizeTimer?: number;

  constructor(
    element: HTMLElement,
    context: ConstructorParameters<typeof BaseComponent>[1],
    initial?: ThemeMapState,
  ) {
    super(element, context);
    if (initial) {
      this.mode = initial.mode;
      this.selectedTheme = initial.selectedTheme;
      this.compare = new Set(initial.compare);
      this.narr = { on: initial.narrOn, i: initial.narrI };
    }
  }

  /** Snapshot dello stato corrente (consumato dal router per il back). */
  captureState(): ThemeMapState {
    return {
      mode: this.mode,
      selectedTheme: this.selectedTheme,
      compare: [...this.compare],
      narrOn: this.narr.on,
      narrI: this.narr.i,
    };
  }

  protected async onInit(): Promise<void> {
    this.element.classList.add('theme-map-view');
    // Listener delegato UNICO: gli handler vivono qui, render() ridisegna solo l'HTML.
    this.addEventListener(this.element, 'click', (ev) => this.handleClick(ev));
    this.render();
    // Re-render su resize/rotazione (debounced): la timeline è calcolata in px
    // reali sulla larghezza disponibile, quindi va ricalcolata se cambia.
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(() => {
        window.clearTimeout(this.resizeTimer);
        this.resizeTimer = window.setTimeout(() => this.render(), 200);
      });
      ro.observe(this.element);
      this.addCleanup(() => { ro.disconnect(); window.clearTimeout(this.resizeTimer); });
    }
  }

  protected onLocaleChange(): void { this.render(); }

  // ───────────────────────── dati ─────────────────────────

  private allAuthors(): AuthorCatalog[] { return getDataService().listAuthors(); }

  /** Temi presenti, ordinati: movimenti prima, poi generi, per frequenza desc. */
  private presentThemes(): string[] {
    const count = new Map<string, number>();
    for (const a of this.allAuthors())
      for (const t of a.theme_treatments) count.set(t.theme_id, (count.get(t.theme_id) ?? 0) + 1);
    return [...count.keys()].sort((a, b) => {
      const ka = themeKind(a as ThemeId), kb = themeKind(b as ThemeId);
      if (ka !== kb) return ka === 'movement' ? -1 : 1;
      const ca = count.get(a)!, cb = count.get(b)!;
      if (ca !== cb) return cb - ca;
      return a.localeCompare(b);
    });
  }

  private authorsOfTheme(themeId: string): AuthorCatalog[] {
    return this.allAuthors().filter((a) => a.theme_treatments.some((t) => t.theme_id === themeId));
  }

  /** Passa dal servizio, non dall'oggetto di catalogo: così quando il dossier
   *  è stato caricato il trattamento arriva completo di `explanation`. */
  private treatment(a: AuthorCatalog, themeId: string): ThemeTreatmentBlurb | undefined {
    return getDataService()
      .listThemeTreatments(a.id)
      .find((t) => t.theme_id === themeId);
  }

  private blurb(a: AuthorCatalog, themeId: string): string {
    const t = this.treatment(a, themeId);
    if (!t) return '';
    if (t.map_blurb) return t.map_blurb;
    const plain = (t.explanation ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
    const dot = plain.indexOf('. ');
    return dot > 40 ? plain.slice(0, dot + 1) : plain.slice(0, 160);
  }

  private themeColor(themeId: string): string {
    const k = themeKind(themeId as ThemeId);
    return k === 'movement' ? CATEGORY_COLORS.movement : k === 'genre' ? CATEGORY_COLORS.genre : CATEGORY_COLORS.theme;
  }

  /** Tinta della sfera-autore = epoca letteraria (vedi author-sphere.ts). */
  private sphereColor(a: AuthorCatalog): string { return authorSphereColor(a.periods); }

  private timelineYear(a: AuthorCatalog): { year: number; dated: boolean } {
    return chronoYear(a);
  }

  // ───────────────────────── render ─────────────────────────

  private render(): void {
    const themes = this.presentThemes();
    if (themes.length === 0) {
      this.element.innerHTML = `<p class="tmv-empty">${this.t('themeMap.empty')}</p>`;
      return;
    }
    if (!this.selectedTheme || !themes.includes(this.selectedTheme)) this.selectedTheme = themes[0];

    this.element.innerHTML = `
      <header class="tmv-header">
        <h1 class="tmv-title">${this.t('nav.themeMap')}</h1>
        <div class="tmv-lens" role="tablist">
          <button type="button" class="tmv-lens-btn${this.mode === 'time' ? ' is-active' : ''}" data-mode="time" role="tab" aria-selected="${this.mode === 'time'}">${this.t('themeMap.mode.time')}</button>
          <button type="button" class="tmv-lens-btn${this.mode === 'compare' ? ' is-active' : ''}" data-mode="compare" role="tab" aria-selected="${this.mode === 'compare'}">${this.t('themeMap.mode.compare')}</button>
        </div>
      </header>
      ${this.renderRail(themes)}
      ${this.mode === 'time' ? this.renderTime() : this.renderCompare()}
    `;
  }

  private renderRail(themes: string[]): string {
    const loc = this.context.locale as UiLocale;
    return `<div class="tmv-rail">${themes.map((id) => {
      const on = id === this.selectedTheme;
      return `<button type="button" class="tmv-rail-btn${on ? ' is-active' : ''}" data-ft="${escapeXml(id)}"
        style="--pill:${this.themeColor(id)}">${escapeXml(themeLabel(id as ThemeId, loc))}</button>`;
    }).join('')}</div>`;
  }

  // ───────────────────────── ★ NEL TEMPO ─────────────────────────

  private renderTime(): string {
    const loc = this.context.locale as UiLocale;
    const themeId = this.selectedTheme!;
    const col = this.themeColor(themeId);

    const placed: Placed[] = this.authorsOfTheme(themeId)
      .map((author) => { const { year, dated } = this.timelineYear(author); return { author, year, dated, x: 0 }; })
      .sort((p, q) => p.year - q.year);

    const N = placed.length;
    if (this.narr.i > N - 1) this.narr.i = N - 1;
    if (this.narr.i < 0) this.narr.i = 0;

    const padX = 64, H = 210, nodeY = 116;
    // Larghezza in PIXEL REALI (mobile-safe, standard mobile §): la mappa riempie
    // lo spazio disponibile ma NON scende mai sotto una spaziatura minima leggibile
    // (MIN_GAP). Se non ci sta (telefono / molti autori) la mappa CRESCE e si scorre
    // in orizzontale, invece di rimpicciolire sfere e nomi fino a renderli illeggibili
    // (prima il viewBox fisso 1200 a width:100% si schiacciava su 390px).
    const avail = Math.max(300, (this.element.clientWidth || 1040) - 40);
    const needed = N <= 1 ? avail : 2 * padX + (N - 1) * MIN_GAP;
    const W = Math.max(avail, needed); // px reali → viewBox 1:1, nodi a dimensione vera
    const colW = N <= 1 ? 0 : (W - 2 * padX) / (N - 1);
    placed.forEach((p, i) => { p.x = N === 1 ? W / 2 : padX + i * colW; });

    const revealed = (i: number) => !this.narr.on || i <= this.narr.i;

    // fasce dei secoli (gruppi contigui di nodi con lo stesso secolo)
    const bandTop = 30, bandBot = 190;
    const floorY = nodeY + NODE_R + 7; // quota del "pavimento" su cui posano le sfere
    let bands = '';
    let g = 0;
    while (g < N) {
      const code = centuryCode(placed[g].year);
      let h = g; while (h + 1 < N && centuryCode(placed[h + 1].year) === code) h++;
      const left = g === 0 ? 0 : placed[g].x - colW / 2;
      const right = h === N - 1 ? W : placed[h].x + colW / 2;
      const cx = (left + right) / 2;
      // Niente riempimento grigio per banda (evita "due grigi annidati"): il
      // secolo è segnato solo da un separatore tratteggiato + etichetta; lo
      // sfondo resta quello — unico — della scena.
      bands += `${h === N - 1 ? '' : `<line x1="${right}" y1="${bandTop}" x2="${right}" y2="${bandBot}" stroke="rgba(30,22,18,.10)" stroke-dasharray="2 6"/>`}
        <g class="tmv-band-chip" transform="translate(${cx},${bandTop - 4})">
          <text text-anchor="middle" class="tmv-band-label">${escapeXml(centuryLabel(code, (k) => this.t(k)))}</text>
        </g>`;
      g = h + 1;
    }
    // riga di "pavimento" sotto le sfere → ancora visivamente la scena
    const floor = `<line x1="0" y1="${floorY + 2}" x2="${W}" y2="${floorY + 2}" stroke="url(#tm-floor)" stroke-width="1.5"/>`;

    // filo del tema
    const vis = placed.filter((_, i) => revealed(i));
    let thread = '';
    if (vis.length > 1) {
      let d = `M${vis[0].x},${nodeY}`;
      for (let k = 1; k < vis.length; k++) { const mx = (vis[k - 1].x + vis[k].x) / 2; d += ` Q${mx},${nodeY - 12} ${vis[k].x},${nodeY}`; }
      // filo materico: alone morbido + tratto pieno + filo di luce sopra
      thread = `<path d="${d}" fill="none" stroke="${col}" stroke-width="8" stroke-opacity="0.10" stroke-linecap="round"/>
        <path d="${d}" fill="none" stroke="${col}" stroke-width="3" stroke-opacity="0.55" stroke-linecap="round"/>
        <path d="${d}" fill="none" stroke="rgba(255,255,255,0.55)" stroke-width="1" stroke-opacity="0.6" stroke-linecap="round" transform="translate(0,-1.2)"/>`;
    }

    // frecce di trasmissione (relations.influenced tra autori del tema)
    const byId = new Map(placed.map((p) => [p.author.id, p]));
    const idx = new Map(placed.map((p, i) => [p.author.id, i]));
    let arrows = '';
    for (const p of placed) {
      for (const tid of p.author.relations.influenced ?? []) {
        const s = byId.get(p.author.id), t = byId.get(tid);
        if (!s || !t) continue;
        if (this.narr.on && !(revealed(idx.get(p.author.id)!) && revealed(idx.get(tid)!))) continue;
        const topY = nodeY - 68, mx = (s.x + t.x) / 2;
        // Niente punta di freccia: è una linea temporale, la trasmissione va
        // sempre passato→futuro (sx→dx) ed è già implicita dalla posizione.
        arrows += `<path d="M${s.x},${nodeY - 22} Q${mx},${topY} ${t.x},${nodeY - 22}" fill="none"
          stroke="${col}" stroke-width="1.6" stroke-opacity="0.6" stroke-linecap="round"/>`;
      }
    }

    // nodi autore
    const nodes = placed.map((p, i) => {
      const op = revealed(i) ? (this.narr.on && i !== this.narr.i ? 0.45 : 1) : 0;
      const sub = p.dated ? String(p.year) : escapeXml(centuryLabel(centuryCode(p.year), (k) => this.t(k)));
      return `<g class="tmv-node" data-node-id="${escapeXml(p.author.id)}" style="cursor:pointer;opacity:${op};transition:opacity .35s">
        <ellipse cx="${p.x}" cy="${floorY}" rx="${NODE_R * 0.95}" ry="5" fill="rgba(30,20,15,0.22)" filter="url(#tm-ground)"/>
        ${authorSphere(p.x, nodeY, NODE_R, computeInitials(p.author.name), 16, this.sphereColor(p.author))}
        <text x="${p.x}" y="${nodeY + NODE_R + 18}" text-anchor="middle" class="tmv-node-name">${escapeXml(shortLabel(p.author.name))}</text>
        <text x="${p.x}" y="${nodeY + NODE_R + 33}" text-anchor="middle" class="tmv-node-year">${sub}</text>
      </g>`;
    }).join('');

    const span = N > 1 ? `${placed[0].dated ? placed[0].year : '~' + placed[0].year}–${placed[N - 1].year}` : '—';
    // intestazione del tema in HTML (fuori dall'SVG → niente sovrapposizioni con le fasce)
    const head = `<div class="tmv-time-head">
      <span class="tmv-time-title" style="color:${col}">${escapeXml(themeLabel(themeId as ThemeId, loc))}</span>
      <span class="tmv-time-sub">${escapeXml(nodeKindLabel(themeKind(themeId as ThemeId), loc).toUpperCase())} · ${N} · ${span}</span>
    </div>`;

    // toolbar "Racconta" + caption
    const tlbar = `<div class="tmv-tlbar">
      ${this.narr.on ? `
        <button type="button" class="tmv-tlbar-btn tmv-tlbar-nav" data-narr="prev">◀ ${this.t('themeMap.narrate.prev')}</button>
        <span class="tmv-tlbar-step">${this.t('themeMap.narrate.step')} ${this.narr.i + 1}/${N}</span>
        <button type="button" class="tmv-tlbar-btn tmv-tlbar-nav" data-narr="next">${this.narr.i < N - 1 ? `${this.t('themeMap.narrate.next')} ▶` : this.t('themeMap.narrate.done')}</button>
        <button type="button" class="tmv-tlbar-btn tmv-tlbar-exit" data-narr="toggle">${this.t('themeMap.narrate.exit')} ✕</button>
      ` : `<button type="button" class="tmv-tlbar-btn primary" data-narr="toggle">${this.t('themeMap.narrate.start')}</button>`}
    </div>`;
    const cur = placed[this.narr.i]?.author;
    const caption = this.narr.on && cur ? `<div class="tmv-caption" style="border-color:${col}">
      <span class="tmv-caption-who">${escapeXml(cur.name)}</span>
      <span class="tmv-caption-yr">${placed[this.narr.i].dated ? placed[this.narr.i].year : escapeXml(centuryLabel(centuryCode(placed[this.narr.i].year), (k) => this.t(k)))}</span>
      <div class="tmv-caption-blurb">${escapeXml(this.blurb(cur, themeId))}</div></div>` : '';

    return `${head}${tlbar}
      <div class="tmv-canvas tmv-canvas--time">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" style="width:${W}px;max-width:none"
             role="img" aria-label="${escapeXml(themeLabel(themeId as ThemeId, loc))}">
          <defs>${authorSphereDefs(placed.map((p) => this.sphereColor(p.author)))}
            <linearGradient id="tm-floor" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0" stop-color="rgba(30,22,18,0)"/>
              <stop offset="0.5" stop-color="rgba(30,22,18,0.14)"/>
              <stop offset="1" stop-color="rgba(30,22,18,0)"/>
            </linearGradient>
            <filter id="tm-ground" x="-60%" y="-120%" width="220%" height="340%">
              <feGaussianBlur stdDeviation="2.4"/>
            </filter>
          </defs>
          <g>${bands}</g>${floor}${thread}<g>${arrows}</g><g>${nodes}</g>
        </svg>
      </div>${caption}`;
  }

  // ───────────────────────── ● CONFRONTO ─────────────────────────

  private renderCompare(): string {
    const loc = this.context.locale as UiLocale;
    const themeId = this.selectedTheme!;
    const col = this.themeColor(themeId);
    const sat = this.authorsOfTheme(themeId);

    // cerchio = SELETTORE (compatto); il confronto vero avviene nelle colonne sotto
    const W = 760, H = 330, cx = W / 2, cy = H / 2, R = 118;
    let edges = '', nodes = '';
    sat.forEach((a, i) => {
      const ang = -Math.PI / 2 + (i / sat.length) * Math.PI * 2;
      const x = cx + Math.cos(ang) * R, y = cy + Math.sin(ang) * R;
      const sel = this.compare.has(a.id);
      const dim = this.compare.size > 0 && !sel;
      edges += `<line x1="${cx}" y1="${cy}" x2="${x}" y2="${y}" stroke="${col}" stroke-width="2" stroke-opacity="${dim ? 0.1 : 0.32}"/>`;
      nodes += `<g class="tmv-node" data-cmp-id="${escapeXml(a.id)}" style="cursor:pointer;opacity:${dim ? 0.4 : 1}">
        ${sel ? `<circle cx="${x}" cy="${y}" r="${NODE_R + 8}" fill="none" stroke="${CATEGORY_COLORS.work}" stroke-width="3.5"/>` : ''}
        ${authorSphere(x, y, NODE_R, computeInitials(a.name), 16, this.sphereColor(a))}
        <text x="${x}" y="${y + NODE_R + 18}" text-anchor="middle" class="tmv-node-name">${escapeXml(shortLabel(a.name))}</text>
      </g>`;
    });
    const hubW = Math.max(124, themeLabel(themeId as ThemeId, loc).length * 11 + 40);
    const hub = `<rect x="${cx - hubW / 2}" y="${cy - 28}" width="${hubW}" height="56" rx="28" fill="${col}" filter="url(#auth-sh)"/>
      <text x="${cx}" y="${cy - 3}" text-anchor="middle" class="tmv-hub-title">${escapeXml(themeLabel(themeId as ThemeId, loc))}</text>
      <text x="${cx}" y="${cy + 16}" text-anchor="middle" class="tmv-hub-sub">${escapeXml(nodeKindLabel(themeKind(themeId as ThemeId), loc).toUpperCase())} · ${sat.length}</text>`;

    // colonne di confronto: i trattamenti COMPLETI affiancati — il confronto è QUI
    const authorById = new Map(this.allAuthors().map((a) => [a.id, a]));
    let cols: string;
    if (this.compare.size === 0) {
      cols = `<p class="tmv-cmp-instr">${this.t('themeMap.compare.hint')} «${escapeXml(themeLabel(themeId as ThemeId, loc))}».</p>`;
    } else {
      cols = `<div class="tmv-cmp-cols">${[...this.compare].map((id) => {
        const a = authorById.get(id); if (!a) return '';
        const tr = this.treatment(a, themeId);
        return `<article class="tmv-cmp-col">
          <header class="tmv-cmp-col-head">
            <span class="tmv-cmp-col-name">${escapeXml(a.name)}</span>
            <button type="button" class="tmv-cmp-rm" data-rm="${escapeXml(id)}" aria-label="remove">×</button>
          </header>
          <div class="tmv-cmp-col-blurb" style="border-color:${col}">${escapeXml(this.blurb(a, themeId))}</div>
          <div class="tmv-cmp-col-text">${tr?.explanation ?? ''}</div>
          <button type="button" class="tmv-cmp-col-open" data-open-id="${escapeXml(id)}">${this.t('themeMap.compare.open')} →</button>
        </article>`;
      }).join('')}</div>`;
    }

    return `<div class="tmv-canvas tmv-compare-stage">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" role="img" aria-label="${escapeXml(themeLabel(themeId as ThemeId, loc))}">
          <defs>${authorSphereDefs(sat.map((a) => this.sphereColor(a)))}</defs>
          <g>${edges}</g>${hub}<g>${nodes}</g>
        </svg>
      </div>${cols}`;
  }

  // ───────────────────────── eventi ─────────────────────────

  private handleClick(ev: Event): void {
    const target = ev.target as Element;

    const railBtn = target.closest<HTMLElement>('[data-ft]');
    if (railBtn) { this.selectedTheme = railBtn.dataset.ft!; this.narr = { on: false, i: 0 }; this.compare = new Set(); this.render(); return; }

    const modeBtn = target.closest<HTMLElement>('[data-mode]');
    if (modeBtn) { this.mode = modeBtn.dataset.mode as Mode; this.render(); return; }

    const narrBtn = target.closest<HTMLElement>('[data-narr]');
    if (narrBtn) {
      const N = this.authorsOfTheme(this.selectedTheme!).length;
      const act = narrBtn.dataset.narr;
      if (act === 'toggle') { this.narr = { on: !this.narr.on, i: 0 }; }
      else if (act === 'prev') { this.narr.i = Math.max(0, this.narr.i - 1); }
      else if (act === 'next') { if (this.narr.i < N - 1) this.narr.i++; else this.narr.on = false; }
      this.render(); return;
    }

    const rm = target.closest<HTMLElement>('[data-rm]');
    if (rm) { this.compare.delete(rm.dataset.rm!); this.render(); return; }

    const open = target.closest<HTMLElement>('[data-open-id]');
    if (open) { this.context.bus.emit('navigate:author', { id: open.dataset.openId, themeId: this.selectedTheme ?? undefined }); return; }

    const cmp = target.closest<HTMLElement>('[data-cmp-id]');
    if (cmp) {
      const id = cmp.dataset.cmpId!;
      if (this.compare.has(id)) this.compare.delete(id);
      else if (this.compare.size >= 3) { return; }
      else {
        this.compare.add(id);
        // La colonna compare subito con la sintesi presa dal catalogo; il testo
        // completo del trattamento sta nel dossier, che arriva a richiesta.
        void getDataService().loadAuthor(id).then(() => this.render());
      }
      this.render(); return;
    }

    const node = target.closest<HTMLElement>('[data-node-id]');
    if (node) { this.context.bus.emit('navigate:author', { id: node.dataset.nodeId, themeId: this.selectedTheme ?? undefined }); return; }
  }

  private t(key: string): string { return this.context.i18n.t(key); }
}

// ───────────────────────── helper puri ─────────────────────────

/** Iniziale di secolo: 'medieval' (<1485) oppure il numero di secolo (es. 16). */
function centuryCode(year: number): string {
  if (year < 1485) return 'medieval';
  return String(Math.floor((year - 1) / 100) + 1);
}
function centuryLabel(code: string, t: (k: string) => string): string {
  if (code === 'medieval') return t('themeMap.era.medieval');
  return `${ROMAN[Number(code)] ?? code} ${t('themeMap.era.century')}`;
}

/** Nome corto per il nodo: parte prima della parentesi ("Anonymous (the
 *  Beowulf poet)" → "Anonymous"), altrimenti il cognome. */
function shortLabel(name: string): string {
  const paren = name.indexOf('(');
  if (paren > 0) return name.slice(0, paren).trim();
  const toks = name.trim().split(/\s+/);
  if (/^(sir|lord|lady|dr)\.?$/i.test(toks[0])) return toks.slice(1).join(' ');
  return toks.length > 1 ? toks[toks.length - 1] : name;
}

